// UI Component Imports
import { CustomButton } from "@/components/ui/Buttons";

// Icon Imports
import CompanyIcon from "@/assets/icons/Company.svg";
import CategoryIcon from "@/assets/icons/Category.svg";
import CalenderIcon from "@/assets/icons/CalenderGrayClock.svg";
import VideoIcon from "@/assets/icons/VideoPauseIcon.svg";
import InfoIcon from "@/assets/icons/InfoIcon.svg";
// import Clock from "@/assets/icons/ClockIcon.svg";
import ClockDeadline from "@/assets/icons/ClockDeadline.svg";
import ProductIcon from "@/assets/icons/Product.svg";
import InfoIconBlack from "@/assets/icons/InfoIconBlack.svg";
// import LedProject from "@/assets/icons/LedProject.svg";
import PassionbitsAssuredLogo from "@/assets/logo/PassionbitsAssuredLogo.svg";
import CreatorCanvas from "@/assets/icons/CreatorCanvas.svg";
import MultiActorIcon from "@/assets/icons/multiActorIcon.svg";

// Redux Imports
import { useAppSelector } from "@/redux/hooks/hooks";
import { TJob, TOrderVideo } from "@/redux/feature/jobs/jobsType";
import {
  AcceptJobPayload,
  // useAcceptJobMutation,
  useApplyToJob2Mutation,
} from "@/redux/feature/jobs/jobsApi";

// Utility Imports
import { handleApiRequest } from "@/utils/handleApiRequest";
import { formatDate } from "@/utils/formateData";
import moment from "moment";
import toast from "react-hot-toast";

// Constants
import { Dispatch, SetStateAction, useState } from "react";
import { useGetMeQuery } from "@/redux/feature/user/userApi";
import { useGlobalContext } from "@/context/GlobalContext";
import Modal from "@/components/ui/Modal";
import { DefaultCompanyLogo } from "@/constant";
import { TUCDT } from "@/hooks/useCurrencyByLocation";
import AssembleCastModal from "./AssembleCastModal";
// import ProjectConfirmModal from "./ProjectConfirmModal";
import { FileDetails } from "@/types/types";
import ProjectConfirmationModal from "./ProjectConfirmationModal";
import ProjectConfirmModalHomeStudio from "./ProjectConfirmModal";

import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
type TJobCardProps = {
  additionalPrice: number;
  isMultiActor: boolean;
  isProductAvailable: boolean;
  ucdT: TUCDT;
  currency: string;
  companyLogo: string;
  isCreatorCanvas: boolean;
  projectName: string;
  industry: string;
  price: number;
  amountVideos: number;
  postedAgo: string;
  videoTitle: string;
  handleApplyJob: () => Promise<string | undefined>;
  handleOpenViewDetailsModal: (data: TOrderVideo[]) => void;
  company: string;
  deadlineInDays: number;
  deadlineDate: string;
  videos: any;
  mainVideo: any;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  showApplyButton: boolean;
};

// Main component
const JobsCard = ({
  job,
  filter,
  setVisibleVideos,
  setIsVideoDetailsModalOpen,
}: {
  filter: string;
  setVisibleVideos: React.Dispatch<React.SetStateAction<TOrderVideo[]>>;
  setIsVideoDetailsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  job: TJob;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [applyJob, { isLoading }] = useApplyToJob2Mutation();
  const { user } = useAppSelector((state) => state.auth);
  const { data: userData } = useGetMeQuery(user?.email, { skip: !user?.email });
  const { ucdT } = useGlobalContext();
  const [uploadedVideos, setUploadedVideos] = useState<
    { role?: string; video?: FileDetails; hash?: string; thumbnail?: string }[]
  >([]);

  const handleOpenViewDetailsModal = (data: TOrderVideo[]) => {
    setVisibleVideos(data);
    setIsVideoDetailsModalOpen(true);
  };

  const otherActorData = job?.orderDetails?.actorDetails;
  const showApplyButton = filter === "available" || filter === "invited";

  const isCreatorCanvas = job?.orderDetails?.plan === "CREATOR_CANVAS";
  const projectName = job?.orderDetails?.projectName;
  const isMultiActor = job?.orderDetails?.isMultiActor;
  const currency = job?.orderDetails?.currency;
  const additionalPrice = job?.orderDetails?.additionalPrice;
  const companyLogo = job?.clientDetails?.company_logo || DefaultCompanyLogo;
  const videos = job?.orderDetails?.videos_selectedFor;
  const mainVideo = videos?.[0];
  const amountVideos = job?.orderDetails?.videos_selectedFor?.length;
  const deadlineInDays = job?.orderDetails?.deadlineInDays;

  const deadlineInDate = job?.orderDetails?.deadlineDate
    ? formatDate(job?.orderDetails?.deadlineDate)
    : "No deadline";

  const postedAgo = moment(job?.orderDetails?.postedAt).fromNow();
  const price = job?.orderDetails?.price;
  const videoTitle = job?.orderDetails?.videoTitle;
  const company = job?.clientDetails?.company;
  const industry = job?.clientDetails?.industry;
  const isProductAvailable = job?.orderDetails?.isProductAvailable;

  const handleApplyJob = async (vl?: string) => {
    if (isMultiActor) {
      const isAllVideoUpload =
        otherActorData?.length > 0 &&
        otherActorData.every((actor) =>
          uploadedVideos.some(
            (video) =>
              video.role === actor.actorRole &&
              video.video &&
              video.hash &&
              video.thumbnail,
          ),
        );

      if (!isAllVideoUpload) {
        return toast.error("Please upload all of the cast videos");
      }
    }
    const payload: AcceptJobPayload = {
      video_ids: job.orderDetails.videos_selectedFor.map(
        (item) => item.video_id,
      ),
      email: user?.email as string,
      actor_id: userData?.data?.creator_id,
      supportingCastVideos: uploadedVideos,
      deliverables: vl,
      isSelectedForMultipleVideos:
        job.orderDetails.videos_selectedFor.length > 1 ? true : false,
      order_id: job.orderDetails.order_id,
    };
    await handleApiRequest({
      payload,
      requestFunction: applyJob,
      onSuccess: (response) => {
        toast.success(response.message || "Applied to this job successfully");
        setIsOpen(false);
      },
      onError: (err) => {
        const error = err as {
          data?: { errorSource?: { path?: string; message: string }[] };
        };

        if (error?.data) {
          error?.data?.errorSource?.forEach((item) => {
            if (item.path) {
              const formattedMessage = `${item.path} is ${item.message}`;
              console.log(formattedMessage);
              toast.error(formattedMessage);
            } else {
              console.log(item.message);
              toast.error(item.message);
            }
          });
        }
      },
    });
  };

  const jobDetails = {
    companyLogo,
    currency,
    company,
    isCreatorCanvas,
    projectName,
    videoTitle,
    price,
    postedAgo,
    industry,
    videos,
    mainVideo,
    deadlineInDays,
    deadlineDate: deadlineInDate,
    amountVideos,
    isProductAvailable,
    isMultiActor,
    ucdT,
    additionalPrice,
    handleApplyJob,
    handleOpenViewDetailsModal,
    isLoading,
    setIsOpen,
    showApplyButton,
  };

  return (
    <div>
      <MobileView {...jobDetails} />
      <DesktopView {...jobDetails} />

      <Modal
        isOpen={isOpen}
        preventCloseOnClickOutside
        setOpen={setIsOpen}
        padding="24px"
        width="571px"
      >
        {isMultiActor ? (
          <AssembleCastModal
            isLoading={isLoading}
            setUploadedVideos={setUploadedVideos}
            uploadedVideos={uploadedVideos}
            actorData={otherActorData}
            setOpen={setIsOpen}
            onClick={handleApplyJob}
          />
        ) : (
          <>
            {jobDetails.isCreatorCanvas ? (
              <ProjectConfirmationModal
                isLoading={isLoading}
                setOpen={setIsOpen}
                currency={currency}
                jobPrice={jobDetails?.price}
                onClick={handleApplyJob}
              />
            ) : (
              <ProjectConfirmModalHomeStudio
                isLoading={isLoading}
                setOpen={setIsOpen}
                onClick={handleApplyJob}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default JobsCard;

const MobileView = ({
  companyLogo,
  company,
  isCreatorCanvas,
  projectName,
  price,
  videoTitle,
  postedAgo,
  showApplyButton,
  industry,
  videos,
  deadlineInDays,
  deadlineDate,
  amountVideos,
  mainVideo,
  isLoading,
  currency,
  setIsOpen,
  ucdT,
  isMultiActor,
}: TJobCardProps) => {
  const hasVideos = Array.isArray(videos) && videos.length > 0;
  const firstVideo = hasVideos ? videos[0] : null;
  return (
    <div className="block md:hidden border border-[#E4E7EC] rounded-xl p-4">
      <div>
        <div className="space-y-4 pb-4 border-b border-[#E4E7EC]">
          <div className="flex items-center justify-between">
            {isCreatorCanvas ? <CreatorCanvas /> : <PassionbitsAssuredLogo />}

            <div className="flex items-center gap-1">
              <span>
                <ClockDeadline />
              </span>
              <p className="text-[#667085] text-xs leading-4 tracking-[-2.5%] font-medium">
                Posted {postedAgo}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <h6 className="text-[#000000] text-base leading-6 tracking-[-2.5%] font-semibold">
              {projectName}
            </h6>
            <p className="font-medium text-sm">{videoTitle}</p>
            <div className="flex gap-x-2">
              <div className="flex whitespace-nowrap gap-x-1 items-center">
                <CompanyIcon />
                <p className="text-[#98A2B3] capitalize text-xs leading-4 tracking-[-2.5%] font-medium">
                  {company || "Unknown"}
                </p>
              </div>
              <div className="flex whitespace-nowrap gap-x-1 items-center">
                <CategoryIcon />
                <p className="text-[#98A2B3] capitalize text-xs leading-4 tracking-[-2.5%] font-medium">
                  {industry}
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center flex-wrap gap-3">
            {/* Orientation tag */}
            <p className="bg-[#F2F4F7] px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-1.5%] font-medium whitespace-nowrap">
              {firstVideo?.videoEditingDetails?.format || "N/A"}
            </p>
            {/* Video type tag */}
            <p className="bg-[#F2F4F7] px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-1.5%] font-medium whitespace-nowrap">
              {firstVideo?.scriptDetails?.videoTone?.title || "N/A"}
            </p>
            {/* Duration tag */}
            <p className="bg-[#F2F4F7] px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-1.5%] font-medium whitespace-nowrap">
              {firstVideo?.scriptDetails?.videoLength?.label || "N/A"}
            </p>
            {/* multi actor */}
            {isMultiActor && (
              <div className="flex whitespace-nowrap gap-2 items-center bg-[#ECFDF3] border border-[#47FFB3] rounded-[32px] py-1 px-3">
                <MultiActorIcon />
                <p className="text-[#027143] text-xs leading-5 tracking-[-2.5%] font-medium">
                  Multi-Actor Project
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="border-b border-[#E4E7EC] -mx-4" />
      </div>
      <div className="mt-4">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-[#667085] text-sm leading-[18px] tracking-[0%] font-medium">
              Deadline
            </p>
            <div className="flex items-center gap-x-1">
              <VideoIcon />
              <p className="text-[#667085] text-sm leading-[18px] tracking-[-0%] font-medium">
                {amountVideos} Video
              </p>
            </div>
          </div>
          <div className="flex justify-between">
            {isCreatorCanvas ? (
              <div className=" gap-1 items-center">
                <p className="text-[#000000] text-[16px]  tracking-[-2.5%] font-medium">
                  {deadlineInDays || ""} Days
                </p>
                <span className="text-xs text-[#667085]">
                  (Standard Delivery expected by {deadlineDate})
                </span>
              </div>
            ) : (
              <div className=" gap-x-2">
                <p className="text-[#000000] text-[16px]  tracking-[-2.5%] font-medium">
                  {" "}
                  {deadlineDate}
                </p>
                <span className="text-xs text-[#667085]">
                  (Standard Delivery {deadlineInDays} Days)
                </span>
              </div>
            )}
            <p className="text-lg text-[#1D2939] font-semibold leading-[18px] tracking-[-3%]">
              {getCurrencySymbol(currency)} {price}
            </p>
          </div>
        </div>
        <div className="mt-[18px]">
          <div className="border-t-2 border-[#F4F4F4] -mx-4" />

          <div className="flex gap-x-4 pt-3">
            <CustomButton
              onClick={() => setIsOpen(true)}
              fontSize="14px"
              disabled={!showApplyButton || isLoading}
              fontWeight="500"
              backgroundColor="#FF6600"
              padding="6px 33px"
              borderRadius="32px"
              className="leading-5 tracking-[-2.5%] w-full disabled:opacity-60"
            >
              {!showApplyButton ? (
                "Applied"
              ) : (
                <>{isLoading ? "Applying" : "Apply now"}</>
              )}
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const DesktopView = ({
  companyLogo,
  company,
  isCreatorCanvas,
  projectName,
  currency,
  price,
  postedAgo,
  amountVideos,
  deadlineDate,
  deadlineInDays,
  industry,
  videos,
  mainVideo,
  isLoading,
  setIsOpen,
  videoTitle,
  isMultiActor,
  showApplyButton,
  handleApplyJob,
  handleOpenViewDetailsModal,
  isProductAvailable,
  ucdT,
  additionalPrice,
}: TJobCardProps) => {
  const hasVideos = Array.isArray(videos) && videos.length > 0;
  const firstVideo = hasVideos ? videos[0] : null;
  return (
    <div className="hidden md:block border-[1.3px] border-[#dfddde] rounded-xl">
      <div className="px-4 pt-4 flex justify-between">
        <div>
          <div className="flex gap-x-2 items-start">
            <div className="size-[50px] rounded-lg overflow-hidden ">
              <img
                src={companyLogo}
                alt="Company logo"
                className="h-full w-full rounded-lg object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null; // prevent infinite loop
                  e.currentTarget.src = DefaultCompanyLogo;
                }}
              />
            </div>
            <div>
              {/* Job title and badges */}
              <div className="flex gap-x-2 items-center">
                <div>
                  <h6 className="text-[#000000] text-base leading-6 tracking-[-2.5%] font-semibold">
                    {projectName}
                  </h6>
                  <p className="text-sm font-smibold text-gray-500">
                    {" "}
                    {videoTitle}
                  </p>
                </div>

                {isCreatorCanvas ? (
                  <div className="bg-[#E8EBF2] py-[3px] px-3 rounded-full">
                    <p className="text-[13px] font-semibold leading-[18px] tracking-[-0.5%] bg-clip-text text-transparent bg-gradient-to-l from-[#183979] to-[#FF6600]">
                      Creator Canvas
                    </p>
                  </div>
                ) : (
                  <PassionbitsAssuredLogo />
                )}

                <div className="relative group">
                  {/* Tooltip */}

                  <InfoTooltip>
                    {isCreatorCanvas ? (
                      <p className="text-[#667085] text-xs leading-4 tracking-[0%] font-medium">
                        This is a creator-led video project. You will handle all
                        aspects of production, including recording, editing, and
                        final delivery to the client.
                      </p>
                    ) : (
                      <p className="text-[#667085] text-xs leading-4 tracking-[0%] font-medium">
                        Passionbits Assured assures clients of successful
                        project completion. We assist creators with editing and
                        handle client complaints. Your responsibility is to
                        ensure on-time submissions.
                      </p>
                    )}
                  </InfoTooltip>
                </div>
              </div>

              {/* Company and industry info */}
              <div className="mt-3 flex gap-x-4">
                <div className="flex whitespace-nowrap gap-x-1 items-center">
                  <CompanyIcon />
                  <p className="text-[#98A2B3] capitalize text-xs leading-4 tracking-[-2.5%] font-medium">
                    {company || "Unknown"}
                  </p>
                </div>
                <div className="flex whitespace-nowrap gap-x-1 items-center">
                  <CategoryIcon />
                  <p className="text-[#98A2B3] capitalize text-xs leading-4 tracking-[-2.5%] font-medium">
                    {industry}
                  </p>
                </div>
                {/* Posted time */}
                <div className="flex whitespace-nowrap gap-x-1 items-center">
                  <ClockDeadline />
                  <p className="text-[#667085] capitalize text-xs leading-4 tracking-[-2.5%] font-medium">
                    Posted {postedAgo}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Job requirement tags */}
          <div className="mt-6 flex gap-x-3 mb-5">
            {/* Duration tag */}
            <p className="bg-[#F2F4F7] px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-1.5%] font-medium whitespace-nowrap">
              {firstVideo?.scriptDetails?.videoLength?.label || "N/A"}
            </p>
            {/* Orientation tag */}
            <p className="bg-[#F2F4F7] px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-1.5%] font-medium whitespace-nowrap">
              {firstVideo?.videoEditingDetails?.format || "N/A"}
            </p>
            {/* Video type tag */}
            <p className="bg-[#F2F4F7] px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-1.5%] font-medium whitespace-nowrap">
              {firstVideo?.scriptDetails?.videoTone?.title || "N/A"}
            </p>
            {/* Additional info tag */}

            {amountVideos > 1 && (
              <p
                onClick={() => handleOpenViewDetailsModal(videos)}
                className="bg-[#F2F4F7] cursor-pointer px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-1.5%] font-medium whitespace-nowrap"
              >
                {amountVideos}+
              </p>
            )}
          </div>
          {/* {isMultiActor && (
            <p className="text-[#F66D00] text-xs  bg-[#FFA638]/10 rounded-full px-3 py-1">
              Please note that this is a multi-actor project in [City Name] will
              require travel to different locations within the city.
            </p>
          )} */}
        </div>

        {/* Right Section - Price and free product badge */}
        <div className="flex flex-col items-end space-y-2">
          {/* Price information */}
          <div className="flex gap-x-2 items-center justify-end">
            <VideoIcon />
            <p className="text-[#667085] text-sm leading-5 tracking-[-2.5%] font-medium">
              {amountVideos} Video
            </p>
            <h6 className="text-[#000000] text-xl leading-5 tracking-[-2.5%]  flex items-center  gap-x-2 font-semibold ml-1">
              {getCurrencySymbol(currency)} {price}{" "}
              {isMultiActor && `+ ${additionalPrice}`}{" "}
              {isMultiActor && (
                <span className="border px-3 py-0.5 rounded-full text-[#667085] text-xs">
                  {" "}
                  Additional Actor
                </span>
              )}
            </h6>
          </div>

          <div className="flex items-center gap-x-4 mt-2">
            {/* Free product badge - hidden on mobile */}
            {isProductAvailable && (
              <div className="hidden md:flex whitespace-nowrap gap-2 items-center bg-[#EDF2FF] border border-[#C4D1FF] rounded-[32px] py-1 px-3">
                <ProductIcon />
                <p className="text-[#1A3EA0] text-xs leading-5 tracking-[-2.5%] font-medium">
                  Free product available
                </p>
              </div>
            )}
            {isMultiActor && (
              <div className="hidden md:flex whitespace-nowrap gap-2 items-center bg-[#ECFDF3] border border-[#47FFB3] rounded-[32px] py-1 px-3">
                <MultiActorIcon />
                <p className="text-[#027143] text-xs leading-5 tracking-[-2.5%] font-medium">
                  Multi-Actor Project
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="border-t-[1.5px] border-[#f5f5f5] py-3 px-4 flex justify-between items-center">
        {/* Date and time information */}
        <div className="flex gap-x-3 items-end">
          {/* Due date */}
          <div className="flex items-start whitespace-nowrap gap-[2px]">
            <CalenderIcon />

            <div className="flex gap-x-2 items-center leading-5 tracking-[-2.5%] font-medium text-base">
              <span className="text-[#667085] pl-1">Deadline</span>
              {"   "}

              {isCreatorCanvas ? (
                <div className="flex whitespace-nowrap gap-1 items-center">
                  <p className="text-[#000000] text-[16px]  tracking-[-2.5%] font-medium">
                    {deadlineInDays} Days
                  </p>
                  <span className="text-xs text-[#667085]">
                    (Standard Delivery expected by {deadlineDate})
                  </span>
                </div>
              ) : (
                <div className="flex gap-x-2">
                  <p className="text-[#000000] text-[16px]  tracking-[-2.5%] font-medium">
                    {" "}
                    {deadlineDate}
                  </p>
                  <span className="text-xs text-[#667085]">
                    (Standard Delivery {deadlineInDays} Days)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Deadline time */}
        </div>

        {/* Action buttons */}
        <div className="flex gap-x-4">
          <CustomButton
            onClick={() => setIsOpen(true)}
            fontSize="14px"
            disabled={!showApplyButton || isLoading}
            fontWeight="500"
            backgroundColor="#FF6600"
            padding="6px 33px"
            borderRadius="32px"
            className="leading-5 tracking-[-2.5%] w-full disabled:opacity-60"
          >
            {!showApplyButton ? (
              "Applied"
            ) : (
              <>{isLoading ? "Applying" : "Apply now"}</>
            )}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

const InfoTooltip = ({ children }: { children: React.ReactNode }) => (
  <div className="relative group">
    <div className="cursor-pointer">
      <InfoIcon />
    </div>
    <div className="absolute z-10 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-300 left-0 top-full mb-1 w-[295px] ">
      <div className="flex gap-x-3 bg-white px-3 py-2.5 rounded-lg shadow-md border border-gray-100">
        <span>
          <InfoIconBlack />
        </span>
        <p className="text-[#667085] text-xs leading-4 tracking-[0%] font-medium">
          {children}
        </p>
      </div>
    </div>
  </div>
);
